/**
 * Dispatcher for AI `client_action` payloads. Returns a single
 * `fire(action)` function that maps each variant to its concrete
 * client capability:
 *
 *   navigate → slug resolver in navigateForSlug.ts
 *   call     → Linking.openURL('tel:…')
 *   copy     → Clipboard.setStringAsync + toast
 *   share    → useShareReport (targeted method if both shareMethod
 *              and shareTarget are set; otherwise the action sheet)
 *
 * Used by `ClientActionWidget` for both auto-fire (once per
 * messageId+blockIndex) and tap-to-fire.
 */

import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { ReportContentType } from '@euler/model/ReportContentType';
import {
  AIClientAction,
  AIClientActionCall,
  AIClientActionCopy,
  AIClientActionNavigate,
  AIClientActionReportType,
  AIClientActionShareMethod,
  AIClientActionShareReport,
} from '@euler/model/ai';
import { reportContentTypeToReportTabKey } from '@euler/model/util';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import * as Clipboard from 'expo-clipboard';
import { Linking } from 'react-native';
import Toast from 'react-native-root-toast';
import { useAIAssistantActions } from './AIAssistantProvider';
import { navigateForSlug } from './navigateForSlug';

export function useFireClientAction() {
  // The AI overlay renders OUTSIDE both NavigationContainer and the
  // NavigationContainerRefProvider (sibling of RoutesContainer in
  // App.tsx). Both `useNavigation()` and `useNavigationRef()` fail
  // there. `getGlobalNavigationRef()` reads a module-level ref
  // assigned inside RoutesContainer render, which works from any
  // caller once the app has booted.
  const { reportService, serviceRecordService } = useServiceFactory();
  const share = useShareReport();
  const assistant = useAIAssistantActions();

  return usePersistFn(async (action: AIClientAction): Promise<void> => {
    const navigation = getGlobalNavigationRef();
    if (!navigation) {
      onErrorAlert(new Error('navigation not ready'));
      return;
    }
    switch (action.action) {
      case 'navigate':
        // Close the AI panel first — otherwise the user tap-navigates
        // but the panel keeps covering the screen they just opened.
        assistant.close();
        await fireNavigate(action, {
          navigation,
          reportService,
          serviceRecordService,
        });
        return;
      case 'call':
        fireCall(action);
        return;
      case 'copy':
        await fireCopy(action);
        return;
      case 'share':
        await fireShare(action, share);
        return;
    }
  });
}

async function fireNavigate(
  action: AIClientActionNavigate,
  ctx: {
    navigation: ReturnType<typeof getGlobalNavigationRef>;
    reportService: ReturnType<typeof useServiceFactory>['reportService'];
    serviceRecordService: ReturnType<
      typeof useServiceFactory
    >['serviceRecordService'];
  },
): Promise<void> {
  try {
    await navigateForSlug(ctx, action);
  } catch (e) {
    onErrorAlert(e);
  }
}

function fireCall(action: AIClientActionCall): void {
  void Linking.openURL(`tel:${action.phone}`).catch(onErrorAlert);
}

async function fireCopy(action: AIClientActionCopy): Promise<void> {
  await Clipboard.setStringAsync(action.text);
  Toast.show(action.toast ?? '已复制', {
    duration: 1000,
    position: Toast.positions.CENTER,
  });
}

async function fireShare(
  action: AIClientActionShareReport,
  share: ReturnType<typeof useShareReport>,
): Promise<void> {
  const tab = reportContentTypeToReportTabKey(
    reportTypeToContentType(action.reportType),
  );
  const args = {
    orderNo: action.orderNo,
    reportNo: action.reportNo,
    licensePlateNo: action.licensePlateNo,
    target: { type: 'report' as const, id: action.reportNo },
    tab,
    ...(action.shareTarget === 'sa' ? { shareType: 'sa' as const } : {}),
  };
  const method = methodIfDeterministic(action);
  if (method == null) {
    await share.shareReportToCustomer(args);
    return;
  }
  switch (method) {
    case 'wechat':
      await share.shareReportViaWeixin(args);
      return;
    case 'wework':
      await share.shareReportViaWeWork(args);
      return;
    case 'sms':
      await share.shareReportViaSms(args);
      return;
  }
}

function methodIfDeterministic(
  action: AIClientActionShareReport,
): AIClientActionShareMethod | null {
  // "deterministic" = AI specified BOTH the method AND the target.
  // Missing either → show action sheet so the user picks.
  if (action.shareMethod == null || action.shareTarget == null) return null;
  return action.shareMethod;
}

function reportTypeToContentType(
  type: AIClientActionReportType,
): ReportContentType {
  switch (type) {
    case 'overall':
      return ReportContentType.Default;
    case 'pre_inspection':
      return ReportContentType.PreInspection;
    case 'inspection':
      return ReportContentType.Inspection;
    case 'construction':
      return ReportContentType.Construction;
    case 'delivery_check':
      return ReportContentType.DeliveryCheck;
    case 'sampling_check':
      return ReportContentType.SamplingCheck;
  }
}
